import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Download, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS, PRODUCTS } from "../data/site";
import { useCatalog } from "../context/CatalogContext";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [prodOpen, setProdOpen] = useState(false);
    const location = useLocation();
    const { open: openCatalog } = useCatalog();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Always show solid nav when not on home (other pages don't have dark hero)
    const isHome = location.pathname === "/";
    const forceSolid = !isHome;
    const solid = scrolled || forceSolid;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                solid ? "glass-nav" : "bg-transparent"
            }`}
        >
            <div
                data-testid="top-utility-bar"
                className={`border-b transition-colors ${
                    solid ? "border-black/5 bg-white/60 text-ink/80" : "border-white/10 bg-black/35 text-white/85 backdrop-blur"
                }`}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-1.5 text-[11px] font-medium tracking-wide sm:px-8 lg:px-10">
                    <span data-testid="top-gstn" className="flex items-center gap-2 truncate">
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${solid ? "bg-brand-red" : "bg-brand-turmeric"}`} />
                        <span className="uppercase tracking-[0.18em]">GSTN</span>
                        <span className="font-semibold tracking-[0.06em]">{BRAND.gstn}</span>
                    </span>
                    <div className="hidden items-center gap-5 sm:flex">
                        <a href={`mailto:${BRAND.email}`} className="opacity-80 hover:opacity-100">{BRAND.email}</a>
                        <span className="opacity-30">·</span>
                        <a href={`tel:+${BRAND.phoneIntl}`} className="opacity-80 hover:opacity-100">{BRAND.phone}</a>
                        <span className="opacity-30">·</span>
                        <span className="opacity-80">IEC · FSSAI · Spices Board</span>
                    </div>
                </div>
            </div>

            <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 lg:px-10">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-xl bg-black ring-1 ring-black/10">
                        <img src={BRAND.logo} alt="RP Traders" className="h-full w-full object-cover" />
                    </span>
                    <span className="hidden flex-col leading-tight sm:flex">
                        <span className={`font-display text-[15px] font-bold tracking-tight ${solid ? "text-ink" : "text-white"}`}>
                            RP <span className="rp-text-gradient">Traders</span>
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.18em] ${solid ? "text-ink-muted" : "text-white/80"}`}>
                            Spice Exporters · Est. 2016
                        </span>
                    </span>
                </Link>

                <div className="hidden items-center gap-0.5 lg:flex">
                    {/* Products dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setProdOpen(true)}
                        onMouseLeave={() => setProdOpen(false)}
                    >
                        <Link
                            to="/products"
                            data-testid="nav-link-products"
                            className={`inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                                solid ? "text-ink/80 hover:text-ink" : "text-white/90 hover:text-white"
                            }`}
                        >
                            Products <ChevronDown className="h-3.5 w-3.5" />
                        </Link>
                        {prodOpen && (
                            <div data-testid="nav-products-dropdown" className="absolute left-0 top-full w-72 pt-2">
                                <div className="overflow-hidden rounded-2xl border border-hairline bg-white shadow-xl">
                                    {PRODUCTS.map((p) => (
                                        <Link
                                            key={p.slug}
                                            to={`/products/${p.slug}`}
                                            data-testid={`nav-prod-${p.slug}`}
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-ink hover:bg-neutral-bg"
                                        >
                                            <img src={p.image} alt="" className="h-9 w-9 rounded-lg object-cover" />
                                            <div>
                                                <div className="font-medium">{p.name}</div>
                                                <div className="text-[11px] text-ink-muted">{p.tag}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {NAV_LINKS.slice(1).map((l) => {
                        const isAnchor = l.href.startsWith("/#");
                        const Cmp = isAnchor ? "a" : Link;
                        const props = isAnchor ? { href: l.href } : { to: l.href };
                        return (
                            <Cmp
                                key={l.href}
                                {...props}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                                    solid ? "text-ink/80 hover:text-ink" : "text-white/90 hover:text-white"
                                }`}
                            >
                                {l.label}
                            </Cmp>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={openCatalog}
                        data-testid="nav-catalog-cta"
                        className={`hidden items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all md:inline-flex ${
                            solid ? "border border-hairline text-ink hover:bg-black/5" : "border border-white/30 text-white hover:bg-white/10"
                        }`}
                    >
                        <Download className="h-4 w-4" /> Catalog
                    </button>
                    <a
                        href="/#contact"
                        data-testid="nav-quote-cta"
                        className="hidden items-center gap-1.5 rounded-full bg-brand-red px-4 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.03] sm:inline-flex"
                    >
                        Request Quote <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(true)}
                        className={`grid h-10 w-10 place-items-center rounded-full lg:hidden ${
                            solid ? "bg-black/5 text-ink" : "bg-white/15 text-white backdrop-blur"
                        }`}
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </nav>

            {open && (
                <div data-testid="nav-mobile-drawer" className="fixed inset-0 z-[60] overflow-y-auto bg-white">
                    <div className="flex items-center justify-between px-5 py-3">
                        <span className="font-display text-base font-bold">
                            RP <span className="rp-text-gradient">Traders</span>
                        </span>
                        <button
                            data-testid="nav-mobile-close"
                            onClick={() => setOpen(false)}
                            className="grid h-10 w-10 place-items-center rounded-full bg-black/5"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="flex flex-col gap-1 px-6 pb-12 pt-4">
                        <Link
                            to="/products"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-link-products"
                            className="border-b border-hairline py-3 font-display text-2xl font-semibold text-ink"
                        >
                            Products
                        </Link>
                        <div className="grid grid-cols-2 gap-2 border-b border-hairline py-3">
                            {PRODUCTS.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/products/${p.slug}`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-xl bg-neutral-bg px-3 py-2 text-xs font-medium text-ink"
                                >
                                    {p.name}
                                </Link>
                            ))}
                        </div>
                        {NAV_LINKS.slice(1).map((l) => {
                            const isAnchor = l.href.startsWith("/#");
                            const Cmp = isAnchor ? "a" : Link;
                            const props = isAnchor ? { href: l.href } : { to: l.href };
                            return (
                                <Cmp
                                    key={l.href}
                                    {...props}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="border-b border-hairline py-3 font-display text-2xl font-semibold text-ink"
                                >
                                    {l.label}
                                </Cmp>
                            );
                        })}
                        <button
                            onClick={() => {
                                setOpen(false);
                                openCatalog();
                            }}
                            data-testid="nav-mobile-catalog"
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-hairline px-5 py-3.5 text-base font-semibold text-ink"
                        >
                            <Download className="h-4 w-4" /> Download Catalog
                        </button>
                        <a
                            href="/#contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-quote"
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-red px-5 py-3.5 text-base font-semibold text-white"
                        >
                            Request Quote <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
